import React, { useState, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  Container,
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormHelperText,
  DialogActions,
  Switch,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/zustand";
import { useMediaQuery } from "react-responsive";
import CalendarIcon from "../assets/CalendarIcon.png";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate, useLocation } from "react-router-dom";
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";
import TrainingsPlanIcon from "../assets/TrainingsPlanIcon.png";
import { usePreferencesQuery } from "../stores/tanstack/queries/usePreferencesQuery";
import settingsApiService from "../services/api/SettingsApiService";

/**
 * OnboardingStepOne Component
 *
 * First step of the onboarding flow for new users. Allows configuration of:
 * - Exercise preferences (type selection with multi-select dropdown)
 * - Work hours (start time, end time in HH:MM format)
 * - Reminder frequency (30 min to 4 hours)
 * - Work days (Monday through Sunday checkboxes)
 * - Reminder enable/disable toggle
 * - Physio therapy mode (optional, hidden for physio users)
 *
 * On completion, saves exercise configuration to API and navigates to step two (movement settings).
 * Displays introduction dialog on first load and loading state while saving.
 *
 * @returns {JSX.Element} Onboarding step one form with two-column layout
 */
const OnboardingStepOne = () => {
  // Translation and routing
  // Tes commit!
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Time configuration
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");

  // Day selection (1-7: Monday-Sunday)
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]);

  // Exercise type preferences
  const [preferences, setPreferences] = useState([]);
  const [selectedExerciseTypes, setSelectedExerciseTypes] = useState([]);

  // Frequency and reminder settings
  const [frequency, setFrequency] = useState("1");
  const [isEnabled, setIsEnabled] = useState(true);

  // Physio settings
  const [isPhysioEnabled, setIsPhysioEnabled] = useState(false);
  const [isPhysioForced, setIsPhysioForced] = useState(false);

  // Loading and feedback states
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isIntroductionDialogOpen, setIsIntroductionDialogOpen] =
    useState(false);
  const [selectOpen, setSelectOpen] = useState(false);

  // Auth context: current product type and language
  const { currentProduct } = useAuthStore();

  // Responsive layout detection
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Fetch available exercise preferences
  const { data: preferencesData, isLoading: preferencesLoading } =
    usePreferencesQuery();

  // Initialize exercise preferences when fetched from API
  // Excludes "Random" preference type and auto-selects all
  useEffect(() => {
    if (preferencesData?.rawPreferences) {
      const filteredPreferences = preferencesData.rawPreferences.filter(
        (preference) => preference.name !== "Random",
      );

      setPreferences(filteredPreferences);
      setSelectedExerciseTypes(
        filteredPreferences.map((preference) => preference.id),
      );
    }
  }, [preferencesData]);

  // Force physio mode for physio users (non-editable)
  useEffect(() => {
    if (currentProduct === "physio_user") {
      setIsPhysioEnabled(true);
      setIsPhysioForced(true);
    }
  }, [currentProduct]);

  // Show welcome dialog on component load
  useEffect(() => {
    setIsIntroductionDialogOpen(true);
  }, []);

  // Toggle day selection (1-7 for Mon-Sun)
  const handleDaySelection = (event) => {
    const selected = parseInt(event.target.name, 10);
    let updatedSelectedDays;

    if (selectedDays.includes(selected)) {
      updatedSelectedDays = selectedDays.filter((day) => day !== selected);
    } else {
      updatedSelectedDays = [...selectedDays, selected];
    }

    setSelectedDays(updatedSelectedDays);
  };

  // Toggle reminder enable/disable
  const handleReminderToggle = (event) => {
    setIsEnabled(event.target.checked);
  };

  // Toggle physio mode and clear exercise selections
  const handleIsPhysioEnabled = (event) => {
    setIsPhysioEnabled(event.target.checked);
    setSelectedExerciseTypes([]);
  };

  // Exercise type dropdown handlers
  const handleCloseSelect = () => {
    setSelectOpen(false);
  };

  const handleOpenSelect = () => {
    setSelectOpen(true);
  };

  // Save exercise configuration and navigate to step two
  const saveAndNavigateToMovement = async (event) => {
    setIsLoading(true);
    await updateExerciseConfig();
    setIsLoading(false);
    setSnackbarOpen(true);
    const email = location.state?.email;
    navigate("/onboarding-movement", { state: { email } });
  };

  // Update exercise configuration on API
  // Converts HH:MM time format to decimal, includes timezone offset
  async function updateExerciseConfig() {
    const config = {
      startTime: settingsApiService.timeStringToDecimal(startTime),
      endTime: settingsApiService.timeStringToDecimal(endTime),
      frequency: parseFloat(frequency),
      daysOfWeek: selectedDays,
      isEnabled: isEnabled,
      preferences: selectedExerciseTypes,
      isPhysioEnabled: isPhysioEnabled,
      offset: settingsApiService.getFormattedTimezoneOffset(),
    };

    try {
      await settingsApiService.updateExerciseConfig(config);
    } catch (error) {
      console.error("Error updating exercise config:", error);
    }
  }

  // Close snackbar notification
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Two-column layout: welcome message (left), form (right)
  return (
    <Container component="main" maxWidth="lg" sx={{ height: "100vh" }}>
      <CssBaseline />
      {/* Logo positioned at top-left corner */}
      <Typography
        variant="h5"
        sx={{
          position: "absolute",
          top: 16,
          left: 24,
          color: "#fff",
        }}
      >
        <img
          src={DynamicDeskLogo}
          style={{
            width: isMobile ? "24%" : "12%",
            height: isMobile ? "24%" : "12%",
            paddingTop: "12px",
          }}
          alt="DynamicDeskLogo"
        />
      </Typography>
      {/* Main container with two-column layout */}
      <Grid container sx={{ height: "100%" }}>
        {/* Left column: Welcome message */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 8,
            overflowY: "auto",
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            gutterBottom
            sx={{ color: "#fff" }}
          >
            <b>{t("excellantLoggedIn")}</b>
          </Typography>
          <Typography variant="h6" sx={{ color: "#fff", marginTop: "36px" }}>
            <b>{t("withALittleInformation")}</b>
          </Typography>
        </Grid>
        {/* Right column: Onboarding form */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1,
          }}
        >
          <div
            style={{ width: "100vh", padding: "36px" }}
            className={
              isMobile ? "white-background-mobile" : "white-background"
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src={TrainingsPlanIcon}
                style={{ width: "124px", height: "124px", alignSelf: "end" }}
                alt="TrainingsPlanIcon"
              />
              {/* Form title */}
              <Typography
                component="h1"
                variant="h5"
                style={{ color: "#063bd0", alignSelf: "flex-start" }}
              >
                <b>{t("stepOneExercises")}</b>
              </Typography>
              <Typography
                component="h1"
                style={{ color: "#063bd0", alignSelf: "flex-start" }}
              >
                <b>{t("howOftenExercise")}</b>
              </Typography>
              {/* Form grid containing all input fields */}
              <div style={{ marginTop: "36px" }}>
                <Grid container spacing={2}>
                  {/* Exercise type multi-select dropdown - not shown for physio users */}
                  <Box p={1}>
                    {!isPhysioEnabled && (
                      <FormControl error={selectedExerciseTypes.length === 0}>
                        <Select
                          labelId="exercise-type-select-label"
                          id="exercise-type-select"
                          multiple
                          onOpen={handleOpenSelect}
                          open={selectOpen}
                          onClose={handleCloseSelect}
                          value={selectedExerciseTypes}
                          renderValue={(selected) => {
                            if (preferences.length === 0) {
                              return t("loading");
                            }
                            const selectedNames = selected
                              .map((id) => {
                                if (id === "all") {
                                  return t("selectAll");
                                } else {
                                  const preference = preferences.find(
                                    (preference) => preference.id === id,
                                  );
                                  return preference ? preference.name : null;
                                }
                              })
                              .filter(Boolean); // This filters out all falsy values
                            return selectedNames.join(", ");
                          }}
                          onChange={(e) => {
                            // update an array of selected values
                            if (e.target.value.includes("all")) {
                              if (
                                selectedExerciseTypes.length ===
                                preferences.length
                              ) {
                                // if all options are already selected, deselect all
                                setSelectedExerciseTypes([]);
                              } else {
                                // otherwise, select all options
                                setSelectedExerciseTypes(
                                  preferences.map(
                                    (preference) => preference.id,
                                  ),
                                );
                              }
                            } else {
                              setSelectedExerciseTypes(e.target.value);
                            }
                          }}
                          slotProps={{ textField: { size: "small" } }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            // Make text smaller
                            style: { fontSize: 8 },
                          }}
                          size="small"
                          sx={{ width: "395px", fontSize: 14, height: 40 }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxWidth: 20, // where 48 - item height, 4.5 - visible items count, 8 - padding
                              },
                            },
                          }}
                        >
                          <MenuItem key="all" value="all">
                            <Checkbox
                              checked={
                                selectedExerciseTypes.length ===
                                preferences.length
                              }
                            />
                            {t("selectAll")}
                          </MenuItem>
                          {preferences.map((preference) => (
                            <MenuItem key={preference.id} value={preference.id}>
                              <Checkbox
                                checked={
                                  selectedExerciseTypes.indexOf(preference.id) >
                                  -1
                                }
                              />
                              {preference.name}
                            </MenuItem>
                          ))}
                          <div
                            key="confirm"
                            style={{ textAlign: "right", padding: "10px" }}
                          >
                            <Button onClick={handleCloseSelect}>
                              {t("okay")}
                            </Button>
                          </div>
                        </Select>
                        {selectedExerciseTypes.length === 0 && (
                          <FormHelperText>
                            {t("pleaseChooseAtleastOneExercise")}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Box>
                  {/* Start time input - earliest time user wants to receive reminders */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t("startTime")}
                      type="time"
                      value={startTime}
                      onChange={(e) => {
                        setStartTime(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {/* End time input - latest time user wants to receive reminders */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t("endTime")}
                      type="time"
                      value={endTime}
                      onChange={(e) => {
                        setEndTime(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {/* Reminder frequency dropdown - interval between exercise reminders (0.5-4 hours) */}
                  <Grid item xs={12} sm={4}>
                    <FormControl>
                      <Select
                        value={frequency}
                        onChange={(e) => {
                          setFrequency(e.target.value);
                        }}
                      >
                        <MenuItem value="0.5">{t("every30mins")}</MenuItem>
                        <MenuItem value="1">{t("every60mins")}</MenuItem>
                        <MenuItem value="1.5">{t("every90mins")}</MenuItem>
                        <MenuItem value="2">{t("every120mins")}</MenuItem>
                        <MenuItem value="2.5">{t("every150mins")}</MenuItem>
                        <MenuItem value="3">{t("every180mins")}</MenuItem>
                        <MenuItem value="3.5">{t("every210mins")}</MenuItem>
                        <MenuItem value="4">{t("every240mins")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Day selection checkboxes - allows user to choose which days to receive reminders */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Grid container>
                        <Grid
                          item
                          xs={12}
                          container
                          justifyContent="flex-start"
                        >
                          <FormGroup row>
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(1)}
                                  onChange={handleDaySelection}
                                  name="1"
                                />
                              }
                              label={t("monday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(2)}
                                  onChange={handleDaySelection}
                                  name="2"
                                />
                              }
                              label={t("tuesday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(3)}
                                  onChange={handleDaySelection}
                                  name="3"
                                />
                              }
                              label={t("wednesday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(4)}
                                  onChange={handleDaySelection}
                                  name="4"
                                />
                              }
                              label={t("thursday")}
                            />
                          </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                          <FormGroup row>
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(5)}
                                  onChange={handleDaySelection}
                                  name="5"
                                />
                              }
                              label={t("friday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(6)}
                                  onChange={handleDaySelection}
                                  name="6"
                                />
                              }
                              label={t("saturday")}
                            />
                            <FormControlLabel
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.75rem",
                                },
                                marginRight: 1,
                                marginLeft: 1,
                              }}
                              control={
                                <Checkbox
                                  checked={selectedDays.includes(7)}
                                  onChange={handleDaySelection}
                                  name="7"
                                />
                              }
                              label={t("sunday")}
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>
                  <div style={{ paddingLeft: "24px" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEnabled}
                          onChange={handleReminderToggle}
                          name="reminderActivated"
                        />
                      }
                      label={t("reminderActivated")}
                      sx={{ mt: 2, mb: 1, alignSelf: "flex-start" }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      gutterBottom
                      sx={{ alignSelf: "flex-start", mb: 2 }}
                    >
                      {t("switchHere")}
                    </Typography>
                  </div>
                  {!isPhysioForced && (
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <Grid container>
                          <FormGroup row>
                            <Grid container alignItems="center">
                              <Grid item xs={4}>
                                <FormControlLabel
                                  sx={{
                                    "& .MuiTypography-root": {
                                      fontSize: "0.75rem",
                                    },
                                    marginRight: 1,
                                    marginLeft: 1,
                                    "& .MuiFormControlLabel-label": {
                                      color: "#05b67f",
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      checked={isPhysioEnabled}
                                      onChange={handleIsPhysioEnabled}
                                      sx={{
                                        color: "#05b67f",
                                        "&.Mui-checked": { color: "#05b67f" },
                                      }}
                                    />
                                  }
                                  label={t("Physio")}
                                />
                              </Grid>

                              <Grid item xs={8}>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  gutterBottom
                                  sx={{
                                    alignSelf: "flex-start",
                                    mb: 1,
                                    maxWidth: "300px",
                                  }}
                                >
                                  {t("physioCheckbox")}
                                </Typography>
                              </Grid>
                            </Grid>
                          </FormGroup>
                        </Grid>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
                <Button
                  onClick={saveAndNavigateToMovement}
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    float: "right",
                    width: "25%",
                    marginTop: "36px",
                  }}
                >
                  {t("next")}
                </Button>
              </div>
            </Box>
          </div>
        </Grid>
      </Grid>

      {/* Onboarding start dialog */}
      <Dialog
        PaperProps={{
          style: {
            borderRadius: 16,
          },
        }}
        open={isIntroductionDialogOpen}
        onClose={() => setIsIntroductionDialogOpen(false)}
      >
        <DialogTitle>
          <Grid container alignItems="center" spacing={1}>
            {/* Calendar icon for visual context */}
            <Grid item>
              <img
                src={CalendarIcon}
                className="icon-size"
                style={{
                  position: "relative",
                  width: "64px",
                  height: "64px",
                  marginRight: "24px",
                }}
                alt="calendar"
              />
            </Grid>
            <Grid item xs>
              <Typography variant="h6" style={{ color: "#063bd0" }}>
                {t("welcomeToScheduler")}
              </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        {/* Dialog content with welcome message */}
        <DialogContent>{t("accountMadeInroduction")}</DialogContent>
        <DialogActions>
          {/* Close dialog button */}
          <Button onClick={() => setIsIntroductionDialogOpen(false)}>
            {t("next")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading dialog - shown while preferences are being initialized from API */}
      <Dialog
        open={isLoading || preferencesLoading}
        PaperProps={{
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          },
        }}
      >
        <CircularProgress />
        <Typography style={{ marginLeft: "10px" }}>{t("loading")}</Typography>
      </Dialog>

      {/* Success snackbar - shown after form submission to confirm settings were saved */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("settingsSavedSuccessfully")}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OnboardingStepOne;
